import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import apiService from '../../services/api';
import logo from '../../Assets/logo.png';
import './certificateTemplate.scss';
import BeatLoader from 'react-spinners/BeatLoader';

/* One row of the microtext ground — repeated so a row always overflows the
   sheet width and gets clipped, leaving no ragged right edge. */
const MICRO_LINE = Array.from({ length: 14 })
  .map(() => 'KWAHU PRASO COMMUNITY BANK LTD')
  .join(' · ');

const CertificateTemplate = () => {
  const { id } = useParams();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const certRef = useRef(null);

  useEffect(() => {
    const fetchCertificate = async () => {
      try {
        const data = await apiService.getCertificateById(id);
        if (data) {
          setCertificate(data);
        } else {
          setError('Certificate not found');
        }
      } catch (err) {
        console.error('Error fetching certificate:', err);
        setError('Failed to load certificate');
      } finally {
        setLoading(false);
      }
    };

    fetchCertificate();
  }, [id]);

  /* The artwork is fixed-pixel (1060px wide) and taller than one A4 landscape
     sheet's printable area, so the browser used to spill it onto a second
     page. Measure it and set a zoom factor that fits both dimensions —
     a hard-coded factor would drift the moment the layout changes. */
  const fitForPrint = useCallback(() => {
    const el = certRef.current;
    if (!el) return;
    const PAGE_MM = { w: 297, h: 210 }; // A4 landscape
    const MARGIN_MM = 6; // must match @page margin in the print stylesheet
    const pxPerMm = 96 / 25.4;
    const availW = (PAGE_MM.w - MARGIN_MM * 2) * pxPerMm;
    const availH = (PAGE_MM.h - MARGIN_MM * 2) * pxPerMm;

    // offsetWidth/Height include padding and border under either box-sizing
    const scale = Math.min(1, availW / el.offsetWidth, availH / el.offsetHeight);
    el.style.setProperty('--print-scale', scale.toFixed(4));
  }, []);

  // Covers Ctrl+P and the browser menu, not just our own button
  useEffect(() => {
    window.addEventListener('beforeprint', fitForPrint);
    return () => window.removeEventListener('beforeprint', fitForPrint);
  }, [fitForPrint]);

  const handlePrint = () => {
    fitForPrint();
    window.print();
  };

  const handleSavePDF = async () => {
    if (!certRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(certRef.current, {
        scale: 3, // 3× pixel density → sharp text & visible pattern
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: '#ffffff',
        imageTimeout: 15000,
      });

      // A4 landscape: 297 × 210 mm
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
      });
      const margin = 8;
      const maxW = 297 - margin * 2;
      const maxH = 210 - margin * 2;

      const imgRatio = canvas.width / canvas.height;
      const pageRatio = maxW / maxH;
      const drawW = imgRatio > pageRatio ? maxW : maxH * imgRatio;
      const drawH = imgRatio > pageRatio ? maxW / imgRatio : maxH;

      /* PNG, not JPEG. JPEG chroma subsampling averages colour over 2×2 pixel
         blocks, which is exactly what erases 1px guilloché lines and microtext
         — the faintest, most important marks on the sheet. Lossless costs file
         size but keeps the security features intact through to the printer. */
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(
        imgData,
        'PNG',
        margin + (maxW - drawW) / 2,
        margin + (maxH - drawH) / 2,
        drawW,
        drawH,
      );

      pdf.save(
        `Share_Certificate_${certificate?.certificate_number || 'export'}.pdf`,
      );
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  /* 1st, 2nd, 3rd, 4th … 11th/12th/13th are the exceptions that trip the
     naive "last digit" rule, so they are checked first. */
  const ordinalSuffix = (day) => {
    if (day % 100 >= 11 && day % 100 <= 13) return 'th';
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  const formatDateParts = (dateString) => {
    if (!dateString)
      return {
        day: '....',
        daySuffix: '',
        month: '............',
        year: '....',
      };
    const date = new Date(dateString);
    const day = date.getDate();
    return {
      day: day.toString(),
      daySuffix: ordinalSuffix(day),
      month: date.toLocaleDateString('en-GB', { month: 'long' }),
      year: date.getFullYear().toString(),
    };
  };

  if (loading) {
    return (
      <div className="loaderContainer">
        <BeatLoader color="#14539a" />
        <p>Loading certificate...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="certificateError">
        <h2>Error</h2>
        <p>{error}</p>
        <Link to="/certificates" className="backLink">
          ← Back to Certificates
        </Link>
      </div>
    );
  }

  const dateParts = formatDateParts(certificate?.issued_date);
  const isRevoked = certificate?.status === 'revoked';

  return (
    <div className="certificatePage">
      {/* Action bar — hidden during print */}
      <div className="certificateActions no-print">
        <Link to="/certificates" className="backBtn">
          ← Back to Certificates
        </Link>
        <div className="actionGroup">
          {isRevoked && (
            <span className="revokedBadge">⚠ CERTIFICATE REVOKED</span>
          )}
          <button
            onClick={handlePrint}
            className="printBtn"
            disabled={isRevoked}
          >
            🖨️ Print
          </button>
          <button
            onClick={handleSavePDF}
            className="pdfBtn"
            disabled={isRevoked || exporting}
          >
            {exporting ? 'Generating…' : '⬇ Save as PDF'}
          </button>
        </div>
      </div>

      {/* Certificate document — captured by html2canvas */}
      <div
        ref={certRef}
        className={`certificateDocument ${isRevoked ? 'revoked' : ''}`}
      >
        {isRevoked && <div className="revokedWatermark">REVOKED</div>}

        <div className="certFrame">
          {/* Left stub — absolutely positioned, rotated via transform (not writing-mode)
               so html2canvas captures it correctly without overflow artifacts */}
          <div className="certStub">
            <div className="stubWrap">
              <div className="stubBand">
                <span className="stubTitle">RECEIPT</span>
                <span className="stubSep">·</span>
                <span className="stubField">
                  <em>Cert No.</em>
                  <strong>{certificate?.certificate_number}</strong>
                </span>
                <span className="stubSep">·</span>
                <span className="stubField">
                  <em>Shares</em>
                  <strong>
                    {Number(certificate?.number_of_shares || 0).toLocaleString(
                      'en-GH',
                    )}
                  </strong>
                </span>
                <span className="stubSep">·</span>
                <span className="stubField">
                  <em>Class</em>
                  <strong>{certificate?.share_class}</strong>
                </span>
                <span className="stubSep">·</span>
                <span className="stubNote">Return to the Bank's Secretary</span>
              </div>
            </div>
          </div>

          {/* Main certificate body */}
          <div className="certBody">
            {/*
              Security-print layers — all REAL elements (html2canvas does not
              capture ::before/::after pseudo-elements, so every decorative
              layer must be an actual div to survive PDF export).
            */}
            {/* Decorative layers all carry .certLayer so the content
                z-index guard can exclude them as a group.
                The engraved ground is a real vector plate (cert-guilloche-bg.svg)
                — composed rosettes beat stacked CSS gradients, which read as
                noise rather than engraving. */}
            <div className="certLayer certGround" aria-hidden="true" />
            <div className="certLayer certSheen" aria-hidden="true" />
            <div className="certLayer certHalo" aria-hidden="true" />
            {/* Ghost logo paints AFTER the halo — the halo's near-white lift
                would otherwise wash the watermark straight back out */}
            <div className="certLayer certGhost" aria-hidden="true">
              <img src={logo} alt="" />
            </div>
            {/* Orderly microtext field — repeated company name, Apex-style */}
            <div className="certLayer certMicroField" aria-hidden="true">
              {Array.from({ length: 34 }).map((_, i) => (
                <span key={i}>{MICRO_LINE}</span>
              ))}
            </div>

            {/* Header — logo, company identity, document title */}
            <div className="certHeader">
              <div className="certTitleBlock">
                <img src={logo} alt="KPRB Logo" className="certLogo" />
                <h1 className="certBankName">KWAHU PRASO COMMUNITY BANK LTD</h1>
                <p className="certIncorp">
                  (Incorporated under the Companies Act, 1963 — Act 179)
                </p>
                <div className="certTitleRow">
                  <span className="titleRule">
                    <i className="dot" />
                  </span>
                  <p className="certDocTitle">SHARE CERTIFICATE</p>
                  <span className="titleRule flip">
                    <i className="dot" />
                  </span>
                </div>
              </div>
            </div>

            {/* Certificate-number / share-count panels — the two facts a
                reader looks for first, given their own defined fields */}
            <div className="certPanels">
              <div className="certField">
                <span className="fieldLabel">Certificate No.</span>
                <span className="fieldCapsule">
                  {certificate?.certificate_number}
                </span>
              </div>
              <div className="certField">
                <span className="fieldLabel">Number of Shares</span>
                <span className="fieldCapsule">
                  {Number(certificate?.number_of_shares || 0).toLocaleString(
                    'en-GH',
                  )}
                </span>
              </div>
              <div className="certField">
                <span className="fieldLabel">Class of Shares</span>
                <span className="fieldCapsule">{certificate?.share_class}</span>
              </div>
            </div>

            <div className="certDivider" />

            {/* Body text */}
            <div className="certContent">
              <p className="certifyLine">This is to certify that</p>

              <p className="holderName">{certificate?.shareholder_name}</p>
              <p className="holderAddress">
                of {certificate?.shareholder_address || 'N/A'}
              </p>

              <p className="shareStatement">
                is / are the Registered Holder(s) of{' '}
                <span className="emphasis">
                  {Number(certificate?.number_of_shares || 0).toLocaleString(
                    'en-GH',
                  )}
                </span>{' '}
                Fully Paid{' '}
                <span className="emphasis">{certificate?.share_class}</span>{' '}
                Shares of <strong>No Par Value</strong> in the capital of{' '}
                <strong>Kwahu Praso Community Bank Ltd.</strong>, subject to the
                Rules and Regulations of the Bank.
              </p>

              <p className="sealLine">
                Given under the Common Seal of the Bank.
              </p>

              <p className="dateLine">
                This{' '}
                <span className="datePart">
                  {dateParts.day}
                  <sup className="ordinal">{dateParts.daySuffix}</sup>
                </span>{' '}
                day of{' '}
                <span className="datePart">{dateParts.month}</span>,{' '}
                <span className="datePart">{dateParts.year}</span>
              </p>
            </div>

            {/* Signatures + common seal (foil placement indicator) */}
            <div className="certSigs">
              <div className="sigBlock">
                <div className="sigLine" />
                <span className="sigRole">Board Secretary</span>
              </div>
              {/* Reserved space — the bank's physical common seal is applied
                  here by hand, so this is a registration guide, not artwork.
                  A printed rosette would show through an embossed seal. */}
              <div className="certSeal">
                <div className="sealWell" />
                <span className="sealCaption">Common Seal</span>
              </div>
              <div className="sigBlock">
                <div className="sigLine" />
                <span className="sigRole">Director</span>
              </div>
            </div>

            <div className="certDivider" />

            {/* Footer — the transfer-restriction note, on its own */}
            <p className="certNote">
              <strong>Note:</strong> No transfer of any portion of the shares
              comprised in this Certificate will be registered until this
              certificate has been delivered to the Bank's Office.
            </p>

            {/* <div className="certBottomRow">
              <span>No. of Transfer: ............</span>
              <span>Examined by: ............</span>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateTemplate;
